import { useEffectOnce } from 'react-use';
import React from 'react';
import DistrictIcon from '@pkgs/components/assets/icons/small/Property-Multiple.svg';
import LocationIcon from '@pkgs/components/assets/icons/small/Pin-Outlined.svg';
import CityIcon from '@pkgs/components/assets/icons/small/City.svg';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { AddressTypeDto } from '@ev-tech/search-modules-api';

import { isSupportedAddressType } from './isSupportedAddressType';
import { HighlightedText } from './HighlightedText';
import { type AutoSuggestOption } from './AutoComplete';
import { ConditionalWrapper } from '../ConditionalWrapper/ConditionalWrapper';
import { useAppContext } from '../../hooks/useAppContext';
import RecentSearchIcon from '../../assets/icons/small/Search_Recent.svg';

import styles from './AutoComplete.module.css';

const getIconPerType = (type?: AddressTypeDto) => {
  switch (type) {
    case AddressTypeDto.country:
    case AddressTypeDto.natural_feature:
    case AddressTypeDto.sublocality:
    case AddressTypeDto.administrative_area_level_1:
    case AddressTypeDto.administrative_area_level_4:
      return LocationIcon;
    case AddressTypeDto.neighborhood:
    case AddressTypeDto.postal_code:
      return DistrictIcon;
    case AddressTypeDto.locality:
      return CityIcon;
    default:
      return LocationIcon;
  }
};

export type AutocompleteProps = {
  option: AutoSuggestOption;
  'data-testid'?: string;
};

export const AutoCompleteOption = ({ option, 'data-testid': dataTestId }: AutocompleteProps): React.ReactElement => {
  const { events, t, LinkComponent } = useAppContext();

  const validPlaceType = option.isProperty ? undefined : option.types.find(isSupportedAddressType);

  const subText = option.isProperty ? option.description : validPlaceType ? t(`search.searchbar.placeType.${validPlaceType}`) : '';

  const Icon = (option.isRecentSearch ? RecentSearchIcon : getIconPerType(validPlaceType)) as React.FunctionComponent;

  useEffectOnce(() => {
    if (!option.isProperty && !validPlaceType) {
      events?.onError?.(
        new Error(
          `The location search term "${option.title}" is in the Options list but the search term's associated placeType "${validPlaceType}" is not in the supported list`,
        ),
      );
    }
  });

  return (
    <ConditionalWrapper
      condition={option.isProperty}
      wrapper={(children) => (
        <LinkComponent className={styles.link} href={option.isProperty ? option.link : ''}>
          {children}
        </LinkComponent>
      )}
    >
      <div className={styles.autoCompleteOptionContainer} data-testid={dataTestId}>
        <div className={styles.iconContainer}>
          <Icon />
        </div>
        <div className={styles.textOverflowWrapper}>
          <HighlightedText text={option.title} />
          {Boolean(subText) && (
            <div className={styles.textWrapper}>
              <Copy text={subText} as="p" size="m" />
            </div>
          )}
        </div>
      </div>
    </ConditionalWrapper>
  );
};

AutoCompleteOption.displayName = 'AutoCompleteOption';
